import { stageSegments, isMultiStage } from "../lib/calendarLayout";
import { isDraggable } from "../lib/calendarDrag";
import { formatTime } from "../lib/time";

/**
 * One booking rendered on the day calendar.
 *
 * Visual contract:
 *  - One outer card per booking — preserves "this is ONE customer
 *    journey" continuity even when the journey has multiple stages.
 *  - Each stage is rendered as an INNER segment, vertically positioned
 *    by its offset_minutes. Occupied stages render solid; processing
 *    stages render with a softer tint + diagonal-stripe pattern so
 *    staff instantly read "stylist is temporarily free here, but the
 *    customer is still in-service".
 *  - When the card is too short to fit stage labels, only the outer
 *    customer + service line is shown (graceful degradation on mobile
 *    / dense days).
 *
 * Pure presentation. No mutations, no API calls.
 */
export default function BookingBlock({
  booking,
  stylistColor = "#1A3626",
  hourPx,
  top,
  heightPx,
  laneIdx = 0,
  laneTotal = 1,
  onClick,
  onDragStart,
  isDraggingThis = false,
  isDone = false,
  showStageLabels = true,
}) {
  const segments = stageSegments(booking);
  const multi = isMultiStage(booking);
  // Single source of truth for drag eligibility — same gating logic
  // also used by CalendarPage's pointermove handler so the two layers
  // agree about what can be moved.
  const draggable = !!onDragStart && isDraggable(booking) && !isDone;

  // Horizontal lane positioning. We keep a 2px gutter between lanes so
  // adjacent overlapping bookings never share a border (avoids the
  // "did the renderer break?" perception).
  const laneWidthPct = 100 / laneTotal;
  const left = `calc(${laneIdx * laneWidthPct}% + 2px)`;
  const width = `calc(${laneWidthPct}% - 4px)`;

  // Accessibility — describe the action available to assistive tech
  // users. Bookings keep their click-to-open-modal affordance regardless.
  const aria = draggable
    ? `Booking for ${booking.customer?.name || "customer"} at ${formatTime(booking.start_at)}. Click to open or drag to reschedule.`
    : `Booking for ${booking.customer?.name || "customer"} at ${formatTime(booking.start_at)}. Click to open.`;

  return (
    <button
      onClick={(e) => {
        // Drag handler suppresses click via stopPropagation when a drag
        // actually started; bare clicks fall through to the modal.
        onClick?.(booking, e);
      }}
      onPointerDown={(e) => {
        if (!draggable) return;
        // Only initiate drag on primary button. Right-click / middle
        // click should pass through.
        if (e.button !== 0) return;
        onDragStart(booking, e);
      }}
      data-testid={`day-booking-${booking.id}`}
      data-booking-id={booking.id}
      data-lane={laneIdx}
      data-lane-total={laneTotal}
      data-multi-stage={multi ? "true" : "false"}
      data-draggable={draggable ? "true" : "false"}
      aria-label={aria}
      className={`absolute rounded-md text-left overflow-hidden border-2 transition-all duration-200 hover:shadow-lg hover:z-20 focus:z-20 ${
        isDone ? "opacity-60" : ""
      } ${isDraggingThis ? "opacity-30" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{
        top: Math.max(0, top),
        height: Math.max(28, heightPx - 2),
        left,
        width,
        borderColor: stylistColor,
        background: "white",
        touchAction: draggable ? "none" : undefined,
      }}
    >
      {/* Per-stage segments, absolutely positioned by offset. */}
      <div className="absolute inset-0">
        {segments.map((seg, i) => {
          const segTop = (seg.offsetMin / 60) * hourPx;
          const segH = Math.max(2, (seg.durationMin / 60) * hourPx);
          const isProc = seg.type === "processing";
          return (
            <div
              key={seg.key}
              data-testid={`stage-segment-${booking.id}-${i}`}
              data-stage-type={seg.type}
              className="absolute left-0 right-0"
              style={{
                top: segTop,
                height: segH,
                background: isProc
                  ? `repeating-linear-gradient(135deg, rgba(209,224,215,0.55) 0 6px, rgba(209,224,215,0.25) 6px 12px)`
                  : isDone
                  ? "#EFEBE3"
                  : "#D1E0D7",
                borderTop: i === 0 ? "none" : `1px dashed ${stylistColor}40`,
              }}
            >
              {/* Processing badge — only when the segment has room. */}
              {isProc && showStageLabels && segH >= 22 && (
                <div className="absolute top-1 right-1 text-[9px] font-mono uppercase tracking-wide text-[#1A3626]/70 bg-white/60 px-1 rounded-sm">
                  processing
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Foreground: customer + service text, always rendered on top
          of the segment fills so it survives the diagonal stripes. */}
      <div className="relative z-10 px-2 py-1.5 pointer-events-none">
        <div className="text-xs font-medium truncate flex items-center gap-1">
          {isDone && <span className="text-[9px]" aria-label="completed">✓</span>}
          {booking.customer?.name || "—"}
        </div>
        <div className="text-[10px] text-foreground/70 truncate">
          {booking.service?.name || booking.service_name_snapshot} · {formatTime(booking.start_at)}
        </div>
        {multi && showStageLabels && heightPx >= 56 && (
          <div className="text-[9px] font-mono uppercase tracking-wide text-[#1A3626]/60 mt-0.5">
            {segments.length} stages
          </div>
        )}
      </div>
    </button>
  );
}
